import { useRef, useEffect } from 'react';
import type { KeyboardEvent, SyntheticEvent, MouseEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { DropdownMenu, IconButton, Text, TextInput, Tooltip, tokens } from '@hotjar/hotsauce';

import { useTranslation } from 'react-i18next';
import type { SidebarItem, SidebarItemAction } from './SideBar';

export interface ListItemProps<T> {
  item: T;
  actions?: SidebarItemAction<T>[];
  isReadOnly?: boolean;
  onClick: () => void;
  onItemSave?: (item: T) => void;
  onItemBlur?: () => void;
}

const ItemMenuButton = styled(IconButton)`
  display: none;
  pointer-events: none;
  margin: -${tokens.spacer.x0} 0;
  button:hover {
    // TODO: Remove this override when the DS team supports this.
    background-color: ${tokens.BRAND_COLORS.neutral_100} !important;
  }
`;

const Item = styled.li<{ isActive?: boolean; isEditing?: boolean }>`
  position: relative;
  min-height: ${tokens.spacer.x5};
  max-height: ${tokens.spacer.x6};
  padding: ${tokens.spacer.x1};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: flex;
  align-items: center;
  border-radius: ${tokens.radius.l};
  cursor: pointer;
  justify-content: space-between;

  &:not(:last-child) {
    margin-bottom: ${tokens.spacer.x0pt5};
  }

  &:hover {
    background-color: ${tokens.BRAND_COLORS.neutral_100};
    ${ItemMenuButton} {
      display: initial;
      pointer-events: auto;
    }
  }

  ${({ isActive }) =>
    isActive === true &&
    css`
      background-color: ${tokens.BRAND_COLORS.cobalt_200};
      color: ${tokens.color.secondary_cta};
      font-weight: 500;
    `}

  ${({ isEditing }) =>
    isEditing === true &&
    css`
      background-color: transparent;
      padding: 0;
      border-radius: 0;
      overflow: visible;
      cursor: unset;
      width: 100%;
      display: block;
      &:hover {
        background-color: transparent;
      }
    `}
`;

const ItemText = styled(Text)`
  word-break: break-all;
  display: -webkit-box;
  max-width: 200px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: ${tokens.spacer.x4};
  box-sizing: content-box;
  margin: 0;
  padding: 0;
`;

export const ListItem = <T extends SidebarItem>({
  item,
  actions,
  isReadOnly,
  onClick,
  onItemSave,
  onItemBlur,
}: ListItemProps<T>): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const inputRef = useRef<HTMLInputElement>(null);
  const { isEditing } = item;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  if (isReadOnly) {
    return (
      <Item isActive={item.isActive} onClick={onClick}>
        <Tooltip text={item.name}>
          <ItemText as="p" textType="caption">
            {item.name}
          </ItemText>
        </Tooltip>
      </Item>
    );
  }

  const onAction = (event: SyntheticEvent<HTMLButtonElement>, action: SidebarItemAction<T>) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    action.cb(item);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Escape'].includes(event.key)) {
      if (event.key === 'Escape' && inputRef?.current?.value) {
        inputRef.current.value = item.name;
      }
      event.currentTarget.blur();
    }
  };

  const handleBlur = (event: SyntheticEvent<HTMLInputElement>) => {
    onItemBlur?.();
    const value = String(event.currentTarget.value).trim();
    if (value.length && value !== item.name) {
      onItemSave?.({ ...item, name: value });
    }
  };

  return (
    <Item isActive={item.isActive} isEditing={isEditing} onClick={onClick}>
      {isEditing ? (
        <TextInput
          size="s"
          ref={inputRef}
          name="item_title"
          id={`input_${item.id}`}
          defaultValue={item.name}
          placeholder={item.name}
          onKeyDown={onKeyDown}
          onBlur={handleBlur}
        />
      ) : (
        <Tooltip text={item.name}>
          <ItemText as="p" textType="caption">
            {item.name}
          </ItemText>
        </Tooltip>
      )}
      {actions && !isEditing && (
        <DropdownMenu
          size="s"
          trigger={
            <ItemMenuButton
              kind="ghost"
              size="s"
              icon="menu_kebab"
              tooltipText={t('sidebar.show-actions')}
              description={t('sidebar.show-actions')}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
              }}
            />
          }
        >
          {actions.map((action) => (
            <DropdownMenu.Item
              key={action.name}
              icon={action.icon}
              hasDivider={action.hasDivider}
              kind={action.kind}
              onClick={(event) => onAction(event, action)}
            >
              {action.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu>
      )}
    </Item>
  );
};
